import {
  deleteItem as deleteIndexedItem,
  getItem as getIndexedItem,
  setItem as setIndexedItem,
} from '../../../e2ee/storage';

const memoryStore = new Map();
const updateQueues = new Map();
const operationQueues = new Map();

const INDEXED_STORAGE_PREFIXES = ['messenger-v2:mls:identity:', 'messenger-v2:mls:groups:'];

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const hasBrowserStorage = () => {
  try {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  } catch {
    return false;
  }
};

const hasIndexedStorage = () => {
  try {
    return typeof indexedDB !== 'undefined' && Boolean(indexedDB);
  } catch {
    return false;
  }
};

const shouldUseIndexedStorage = (key) =>
  INDEXED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));

const serialize = (value) => {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const deserialize = (value) => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const getItem = async (key) => {
  const normalizedKey = toStringOrNull(key);
  if (!normalizedKey) return null;
  if (shouldUseIndexedStorage(normalizedKey) && hasIndexedStorage()) {
    try {
      const indexedValue = await getIndexedItem(normalizedKey);
      if (indexedValue !== undefined && indexedValue !== null) {
        return indexedValue;
      }

      if (hasBrowserStorage()) {
        const legacyValue = deserialize(window.localStorage.getItem(normalizedKey));
        if (legacyValue !== null) {
          await setIndexedItem(normalizedKey, legacyValue);
          window.localStorage.removeItem(normalizedKey);
          return legacyValue;
        }
      }
    } catch {}
  }
  if (hasBrowserStorage()) {
    try {
      return deserialize(window.localStorage.getItem(normalizedKey));
    } catch {}
  }
  return memoryStore.has(normalizedKey) ? deserialize(memoryStore.get(normalizedKey)) : null;
};

export const setItem = async (key, value) => {
  const normalizedKey = toStringOrNull(key);
  if (!normalizedKey) return;
  const serialized = serialize(value);
  let indexedError = null;
  if (shouldUseIndexedStorage(normalizedKey) && hasIndexedStorage()) {
    try {
      if (serialized == null) {
        await deleteIndexedItem(normalizedKey);
      } else {
        await setIndexedItem(normalizedKey, deserialize(serialized));
      }
      if (hasBrowserStorage()) {
        window.localStorage.removeItem(normalizedKey);
      }
      return;
    } catch (error) {
      indexedError = error;
    }
  }
  if (hasBrowserStorage()) {
    try {
      if (serialized == null) {
        window.localStorage.removeItem(normalizedKey);
      } else {
        window.localStorage.setItem(normalizedKey, serialized);
      }
      return;
    } catch (error) {
      if (shouldUseIndexedStorage(normalizedKey)) {
        throw indexedError || error;
      }
    }
  }
  if (serialized == null) {
    memoryStore.delete(normalizedKey);
  } else {
    memoryStore.set(normalizedKey, serialized);
  }
};

const runWithBrowserLock = async (key, task) => {
  const lockManager =
    typeof navigator !== 'undefined' && navigator?.locks?.request ? navigator.locks : null;
  if (!lockManager) {
    return task();
  }
  return lockManager.request(`nettgrav:storage:${key}`, { mode: 'exclusive' }, task);
};

// All read-modify-write operations for a persisted value pass through this
// boundary. The in-realm queue covers concurrent managers and Web Locks extend
// the same serialization guarantee to tabs sharing this origin.
export const updateItem = async (key, updater) => {
  const normalizedKey = toStringOrNull(key);
  if (!normalizedKey || typeof updater !== 'function') return null;

  const previousQueue = updateQueues.get(normalizedKey) || Promise.resolve();
  const operation = previousQueue
    .catch(() => null)
    .then(() =>
      runWithBrowserLock(normalizedKey, async () => {
        const previous = await getItem(normalizedKey);
        const next = await updater(previous);
        if (next === undefined) return previous;
        await setItem(normalizedKey, next);
        return next;
      }),
    );

  updateQueues.set(normalizedKey, operation);
  try {
    return await operation;
  } finally {
    if (updateQueues.get(normalizedKey) === operation) {
      updateQueues.delete(normalizedKey);
    }
  }
};

// Holds one logical operation across its complete async lifetime. Recovery
// uses this in addition to its durable lease so healthy tabs cannot rotate or
// consume key material concurrently, even when a request exceeds the lease's
// normal polling window.
export const withItemLock = async (key, task) => {
  const normalizedKey = toStringOrNull(key);
  if (!normalizedKey || typeof task !== 'function') return null;

  const lockManager =
    typeof navigator !== 'undefined' && navigator?.locks?.request ? navigator.locks : null;
  if (lockManager) {
    return lockManager.request(`nettgrav:operation:${normalizedKey}`, { mode: 'exclusive' }, task);
  }

  const previousQueue = operationQueues.get(normalizedKey) || Promise.resolve();
  const operation = previousQueue.catch(() => null).then(task);
  operationQueues.set(normalizedKey, operation);
  try {
    return await operation;
  } finally {
    if (operationQueues.get(normalizedKey) === operation) {
      operationQueues.delete(normalizedKey);
    }
  }
};

export const deleteItem = async (key) => {
  const normalizedKey = toStringOrNull(key);
  if (!normalizedKey) return;
  if (shouldUseIndexedStorage(normalizedKey) && hasIndexedStorage()) {
    await deleteIndexedItem(normalizedKey).catch(() => null);
  }
  if (hasBrowserStorage()) {
    try {
      window.localStorage.removeItem(normalizedKey);
    } catch {}
  }
  memoryStore.delete(normalizedKey);
};
